import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Search, Plus, Upload, Trash2, Send, Check, X, Mail, RefreshCw, ShieldOff, UserPlus,
  Settings as SettingsIcon, Wifi, WifiOff, ExternalLink,
} from "lucide-react";

const STATES = ["AL", "AR", "FL", "GA", "LA", "MS", "NC", "OH", "OK", "TN", "TX"];
const STATUSES = [
  { v: "sourced", label: "Sourced", color: "bg-[#F3EEE0] text-[#6B6558]" },
  { v: "queued", label: "Queued", color: "bg-[#E5F1E5] text-[#245C25]" },
  { v: "drafted", label: "Drafted", color: "bg-[#FBEFD3] text-[#7A5410]" },
  { v: "approved", label: "Approved", color: "bg-[#C89434] text-[#1A1A1A]" },
  { v: "sent", label: "Sent", color: "bg-[#1A1A1A] text-white" },
  { v: "replied", label: "Replied", color: "bg-[#245C25] text-white" },
  { v: "converted", label: "Converted", color: "bg-[#7A5410] text-white" },
  { v: "opted_out", label: "Opted Out", color: "bg-[#FADCDA] text-[#8A1F1A]" },
  { v: "bounced", label: "Bounced", color: "bg-[#FADCDA] text-[#8A1F1A]" },
];
const chipFor = (v) => STATUSES.find((s) => s.v === v) || STATUSES[0];

const AUDIENCE_META = {
  lender:    { title: "Lender Outreach",    entity: "banks", entitySingular: "bank",
               subtitle: "Ada finds regional banks, enriches contacts, and drafts outreach — you approve every send." },
  investor:  { title: "Investor Outreach",  entity: "investors", entitySingular: "investor",
               subtitle: "Ada finds active real estate investors, enriches acquisitions contacts, and drafts intro emails — you approve every send." },
  builder:   { title: "Builder Outreach",   entity: "builders", entitySingular: "builder",
               subtitle: "Ada finds GCs and homebuilders with active pipelines, enriches contacts, and drafts financing intros — you approve every send." },
  developer: { title: "Developer Outreach", entity: "developers", entitySingular: "developer",
               subtitle: "Ada finds active developers, enriches capital-markets contacts, and drafts capital-stack intros — you approve every send." },
  listing_agent: { title: "Listing Agent Outreach", entity: "listing agents", entitySingular: "listing agent",
               subtitle: "Ada scrapes Crexi + LoopNet for active CRE brokers, enriches their direct dial + email, and drafts financing-partner intros — you approve every send." },
};

export default function AdminLenderProspects({ audience = "lender" }) {
  const meta = AUDIENCE_META[audience] || AUDIENCE_META.lender;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filterState, setFilterState] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [discoverState, setDiscoverState] = useState("TX");
  const [discovering, setDiscovering] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState({});   // {[pid]: {subject, body}} — inline draft edit state
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [instantly, setInstantly] = useState(null);  // {ok, configured, default_campaign_id, campaigns_by_audience}

  const loadInstantly = async () => {
    try {
      const r = await api.get("/admin/instantly/status");
      setInstantly(r.data);
    } catch (e) { /* non-fatal — badge will show as unknown */ }
  };
  useEffect(() => { loadInstantly(); }, []);

  const load = async () => {
    setLoading(true);
    const [r, s] = await Promise.all([
      api.get("/admin/marketplace/prospects", {
        params: { state: filterState || undefined, status: filterStatus || undefined, audience },
      }),
      api.get("/admin/marketplace/prospects/stats", { params: { audience } }),
    ]);
    setRows(r.data || []);
    setStats(s.data || { total: 0, by_status: {} });
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterState, filterStatus, audience]);

  const discover = async () => {
    setDiscovering(true);
    try {
      const r = await api.post("/admin/marketplace/prospects/discover",
                                { state: discoverState, audience });
      toast.success(`Discovered ${r.data.discovered} ${meta.entity} · ${r.data.added} new · ${r.data.skipped_dupes} dupes skipped`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally { setDiscovering(false); }
  };

  const enrich = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/admin/marketplace/prospects/${p.id}/enrich`);
      toast[r.data.found_contact ? "success" : "message"](
        r.data.found_contact
          ? `Found ${r.data.contact_name} · ${r.data.contact_email}`
          : `No contact published for ${p.institution} — enrich manually.`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally { setBusyId(null); }
  };

  const draft = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/admin/marketplace/prospects/${p.id}/draft`, {});
      toast.success("Ada drafted the email — review below and approve.");
      setExpanded((s) => ({ ...s, [p.id]: true }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusyId(null); }
  };

  const approve = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/admin/marketplace/prospects/${p.id}/approve`);
      if (r.data?.instantly_lead_id) {
        toast.success("Sent via Instantly.ai — scheduled per campaign.");
      } else {
        toast.success("Approved — queued for next outreach batch.");
      }
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : d?.message
          || d?.error
          || "Approve failed";
      toast.error(msg);
    } finally { setBusyId(null); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete prospect for ${p.institution}?`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/admin/marketplace/prospects/${p.id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  const suppress = async (p) => {
    if (!p.contact_email) return;
    if (!window.confirm(`Permanently suppress ${p.contact_email}?`)) return;
    try {
      await api.post("/admin/marketplace/suppressions",
                      { email: p.contact_email, reason: "manual",
                        note: `Manually suppressed from ${p.institution}` });
      toast.success("Added to suppression list");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppress failed");
    }
  };

  const counts = useMemo(() => ({
    total: stats.total || 0,
    sourced: stats.by_status?.sourced || 0,
    queued: stats.by_status?.queued || 0,
    drafted: stats.by_status?.drafted || 0,
    approved: stats.by_status?.approved || 0,
    replied: stats.by_status?.replied || 0,
    converted: stats.by_status?.converted || 0,
  }), [stats]);

  return (
    <div className="space-y-6" data-testid="admin-prospects">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase text-[#6B6558] tracking-widest">// Marketplace · Outreach</div>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mt-2">{meta.title}.</h1>
          <p className="text-sm text-[#6B6558] mt-2 max-w-2xl">
            {meta.subtitle} Draft &amp; Approve — tapping <b>Approve</b> pushes the lead
            into your Instantly.ai campaign for scheduled delivery.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <InstantlyBadge data={instantly} onClick={() => setSettingsOpen(true)} />
          <button
            onClick={() => setSettingsOpen(true)}
            className="byrd-btn byrd-btn-outline"
            data-testid="instantly-settings-btn"
          >
            <SettingsIcon size={14} /> Outreach Settings
          </button>
          <button onClick={() => setAddOpen(true)} className="byrd-btn byrd-btn-outline" data-testid="prospect-add-btn">
            <Plus size={14} /> Add manually
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {[
          ["Total", counts.total, "text-[#1A1A1A]"],
          ["Sourced", counts.sourced, "text-[#6B6558]"],
          ["Queued", counts.queued, "text-[#245C25]"],
          ["Drafted", counts.drafted, "text-[#7A5410]"],
          ["Approved", counts.approved, "text-[#C89434]"],
          ["Replied", counts.replied, "text-[#245C25]"],
          ["Converted", counts.converted, "text-[#7A5410]"],
        ].map(([l, v, c]) => (
          <div key={l} className="byrd-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-[#6B6558]">{l}</div>
            <div className={`font-serif text-2xl font-bold mt-1 ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Discover */}
      <div className="byrd-card p-6 border-l-4 border-[#C89434]" data-testid="discover-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Ada Sourcing</div>
            <h3 className="font-serif text-xl font-bold mt-0.5">Discover {meta.entity} in a state.</h3>
            <p className="text-xs text-[#6B6558] mt-1 max-w-md">
              {meta.subtitle}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={discoverState} onChange={(e) => setDiscoverState(e.target.value)}
                    data-testid="discover-state"
                    className="h-10 px-3 border border-[#E4DFD1] bg-white rounded-md text-sm">
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={discover} disabled={discovering}
                    data-testid="discover-btn"
                    className="byrd-btn byrd-btn-dark disabled:opacity-60">
              <Sparkles size={14} /> {discovering ? "Ada is searching…" : "Discover"}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">Filter:</div>
        <select value={filterState} onChange={(e) => setFilterState(e.target.value)}
                data-testid="filter-state"
                className="h-8 px-2 border border-[#E4DFD1] bg-white rounded-md text-xs">
          <option value="">All states</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="filter-status"
                className="h-8 px-2 border border-[#E4DFD1] bg-white rounded-md text-xs">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <button onClick={load} className="byrd-btn byrd-btn-outline h-8 px-2 text-xs" data-testid="reload-btn">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Prospect table */}
      <div className="byrd-card overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[#6B6558]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B6558]">
            No prospects yet. Tap <b>Discover</b> above to seed a state, or add one manually.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF8F1] text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">
              <tr className="text-left">
                <th className="p-3">Bank</th>
                <th className="p-3">Contact</th>
                <th className="p-3">State</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4DFD1]">
              {rows.map((p) => {
                const chip = chipFor(p.status);
                return (
                  <React.Fragment key={p.id}>
                    <tr className="hover:bg-[#FBF8F1]/60" data-testid={`prospect-row-${p.id}`}>
                      <td className="p-3 align-top">
                        <div className="font-semibold">{p.institution}</div>
                        {p.hq_city && <div className="text-[11px] text-[#6B6558]">{p.hq_city}</div>}
                        {p.website && (
                          <a href={p.website} target="_blank" rel="noreferrer" className="text-[11px] text-[#C89434] hover:underline">
                            {p.website.replace(/^https?:\/\//, "").split("/")[0]}
                          </a>
                        )}
                      </td>
                      <td className="p-3 align-top">
                        {p.contact_name ? (
                          <>
                            <div className="font-medium">{p.contact_name}</div>
                            <div className="text-[11px] text-[#6B6558]">{p.contact_title}</div>
                            {p.contact_email && (
                              <div className="text-[11px] text-[#1A1A1A] mt-0.5">
                                <a href={`mailto:${p.contact_email}`} className="hover:underline">{p.contact_email}</a>
                                {p.email_source_note && (
                                  <div className="text-[10px] text-[#6B6558] italic" title="How Ada found this">
                                    ↳ {p.email_source_note}
                                  </div>
                                )}
                              </div>
                            )}
                            {p.contact_phone && (
                              <div className="text-[11px] text-[#2A2A2A] mt-0.5">
                                <a href={`tel:${p.contact_phone.replace(/[^0-9+]/g, "")}`} className="hover:underline">{p.contact_phone}</a>
                                {p.phone_source_note && (
                                  <div className="text-[10px] text-[#6B6558] italic">↳ {p.phone_source_note}</div>
                                )}
                              </div>
                            )}
                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                              {p.enrichment_confidence && (
                                <span
                                  data-testid={`confidence-${p.id}`}
                                  className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest ${
                                    p.enrichment_confidence === "high" ? "bg-[#E5F1E5] text-[#245C25]" :
                                    p.enrichment_confidence === "medium" ? "bg-[#FBEFD3] text-[#7A5410]" :
                                    "bg-[#FADCDA] text-[#8A1F1A]"
                                  }`}
                                  title="Ada's confidence in this contact"
                                >
                                  {p.enrichment_confidence}
                                </span>
                              )}
                              {p.enrichment_source_url && (
                                <a href={p.enrichment_source_url} target="_blank" rel="noreferrer"
                                   className="text-[10px] text-[#C89434] hover:underline"
                                   data-testid={`source-${p.id}`}>source ↗</a>
                              )}
                              {(p.enrichment_alternates?.length > 0) && (
                                <span className="text-[10px] text-[#6B6558]">· {p.enrichment_alternates.length} backup</span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="text-[11px] text-[#6B6558] italic">Not enriched</span>
                        )}
                      </td>
                      <td className="p-3 align-top text-xs font-mono">{p.state}</td>
                      <td className="p-3 align-top">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${chip.color}`}
                              data-testid={`prospect-status-${p.id}`}>
                          {chip.label}
                        </span>
                      </td>
                      <td className="p-3 align-top text-right whitespace-nowrap">
                        {!p.contact_email && (
                          <button onClick={() => enrich(p)} disabled={busyId === p.id}
                                  className="byrd-btn byrd-btn-outline h-8 px-2 text-xs disabled:opacity-60"
                                  data-testid={`enrich-${p.id}`} title="Ada finds the LO">
                            <Search size={11} /> Enrich
                          </button>
                        )}
                        {p.contact_email && !p.draft_body && (
                          <button onClick={() => draft(p)} disabled={busyId === p.id}
                                  className="byrd-btn byrd-btn-outline h-8 px-2 text-xs disabled:opacity-60"
                                  data-testid={`draft-${p.id}`} title="Ada writes the email">
                            <Sparkles size={11} /> Draft
                          </button>
                        )}
                        {p.contact_email && (
                          <button
                            onClick={async () => {
                              setBusyId(p.id);
                              try {
                                const r = await api.post(`/admin/marketplace/prospects/${p.id}/save-to-crm`);
                                toast.success(r.data.message || "Saved to Contacts");
                                load();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Save failed");
                              } finally { setBusyId(null); }
                            }}
                            disabled={busyId === p.id}
                            className={`byrd-btn h-8 px-2 text-xs disabled:opacity-60 ${p.saved_to_crm_at ? "byrd-btn-outline text-[#2A5D2A]" : "byrd-btn-outline"}`}
                            data-testid={`save-crm-${p.id}`}
                            title={p.saved_to_crm_at ? "Already in CRM — click to re-sync fields" : `Add to Contacts as a ${meta.entitySingular}`}
                          >
                            <UserPlus size={11} /> {p.saved_to_crm_at ? "In CRM" : "Add to Contacts"}
                          </button>
                        )}
                        {p.draft_body && p.status !== "approved" && p.status !== "sent" && (
                          <button onClick={() => approve(p)} disabled={busyId === p.id}
                                  className="byrd-btn byrd-btn-dark h-8 px-2 text-xs disabled:opacity-60"
                                  data-testid={`approve-${p.id}`} title="Approve for send">
                            <Check size={11} /> Approve
                          </button>
                        )}
                        {(p.draft_body || p.enrichment_alternates?.length > 0) && (
                          <button onClick={() => setExpanded((s) => ({ ...s, [p.id]: !s[p.id] }))}
                                  className="byrd-btn byrd-btn-outline h-8 px-2 text-xs ml-1"
                                  data-testid={`toggle-${p.id}`}
                                  title={p.draft_body ? "Show/hide draft" : "Show backup contacts"}>
                            {expanded[p.id] ? <X size={11} /> : <Mail size={11} />}
                          </button>
                        )}
                        {p.contact_email && (
                          <button onClick={() => suppress(p)}
                                  className="byrd-btn byrd-btn-outline h-8 px-2 text-xs ml-1"
                                  data-testid={`suppress-${p.id}`} title="Never contact this email again">
                            <ShieldOff size={11} />
                          </button>
                        )}
                        <button onClick={() => remove(p)}
                                className="byrd-btn byrd-btn-outline h-8 px-2 text-xs ml-1 hover:bg-[#FADCDA]"
                                data-testid={`delete-${p.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                    {expanded[p.id] && (p.draft_body || p.enrichment_alternates?.length > 0) && (
                      <tr className="bg-[#FBF8F1]">
                        <td colSpan={5} className="p-4 space-y-3">
                          {p.enrichment_alternates?.length > 0 && (
                            <div>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mb-1.5">// Backup contacts Ada found</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {p.enrichment_alternates.map((a, idx) => (
                                  <div key={idx} className="border border-[#E4DFD1] bg-white rounded-md p-2.5 text-xs">
                                    <div className="font-semibold">{a.contact_name}</div>
                                    <div className="text-[#6B6558]">{a.contact_title}</div>
                                    {a.contact_email && <div className="mt-0.5"><a href={`mailto:${a.contact_email}`} className="hover:underline">{a.contact_email}</a></div>}
                                    {a.contact_phone && <div className="text-[11px] text-[#2A2A2A]">{a.contact_phone}</div>}
                                    <div className="mt-1 flex items-center gap-2">
                                      <button
                                        onClick={async () => {
                                          try {
                                            await api.patch(`/admin/marketplace/prospects/${p.id}`, {
                                              contact_name: a.contact_name,
                                              contact_title: a.contact_title,
                                              contact_email: a.contact_email || undefined,
                                              contact_phone: a.contact_phone || undefined,
                                              draft_subject: null,
                                              draft_body: null,
                                              status: a.contact_email ? "queued" : "sourced",
                                            });
                                            toast.success(`Promoted ${a.contact_name} to primary`);
                                            load();
                                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                        }}
                                        data-testid={`use-alternate-${p.id}-${idx}`}
                                        className="byrd-btn byrd-btn-outline h-7 px-2 text-[10px]"
                                      >
                                        Use as primary
                                      </button>
                                      {a.source_url && (
                                        <a href={a.source_url} target="_blank" rel="noreferrer"
                                           className="text-[10px] text-[#C89434] hover:underline">source ↗</a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {p.draft_body && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Ada's draft</div>
                                {p.status !== "sent" && (
                                  <button
                                    type="button"
                                    onClick={() => setEditing((s) => (
                                      s[p.id]
                                        ? (() => { const c = { ...s }; delete c[p.id]; return c; })()
                                        : { ...s, [p.id]: { subject: p.draft_subject || "", body: p.draft_body || "" } }
                                    ))}
                                    className="text-[10px] font-mono uppercase tracking-widest text-[#C89434] hover:text-[#8A6821]"
                                    data-testid={`edit-draft-${p.id}`}
                                  >
                                    {editing[p.id] ? "Cancel" : "Edit"}
                                  </button>
                                )}
                              </div>
                              {editing[p.id] ? (
                                <div className="space-y-2">
                                  <input
                                    value={editing[p.id].subject}
                                    onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], subject: e.target.value } }))}
                                    placeholder="Subject"
                                    maxLength={80}
                                    className="w-full h-9 px-2 border border-[#E4DFD1] rounded-md text-sm"
                                    data-testid={`edit-subject-${p.id}`}
                                  />
                                  <textarea
                                    value={editing[p.id].body}
                                    onChange={(e) => setEditing((s) => ({ ...s, [p.id]: { ...s[p.id], body: e.target.value } }))}
                                    rows={9}
                                    placeholder="Body"
                                    className="w-full px-2 py-2 border border-[#E4DFD1] rounded-md text-sm font-sans"
                                    data-testid={`edit-body-${p.id}`}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        setBusyId(p.id);
                                        try {
                                          await api.patch(`/admin/marketplace/prospects/${p.id}`, {
                                            draft_subject: editing[p.id].subject,
                                            draft_body: editing[p.id].body,
                                          });
                                          toast.success("Draft saved");
                                          setEditing((s) => { const c = { ...s }; delete c[p.id]; return c; });
                                          load();
                                        } catch (e) {
                                          toast.error(e?.response?.data?.detail || "Save failed");
                                        } finally { setBusyId(null); }
                                      }}
                                      disabled={busyId === p.id}
                                      className="byrd-btn byrd-btn-dark text-xs disabled:opacity-60"
                                      data-testid={`save-draft-${p.id}`}
                                    >Save</button>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        setBusyId(p.id);
                                        try {
                                          await api.post(`/admin/marketplace/prospects/${p.id}/draft`, {});
                                          toast.success("Ada re-drafted the email");
                                          setEditing((s) => { const c = { ...s }; delete c[p.id]; return c; });
                                          load();
                                        } catch (e) {
                                          toast.error(e?.response?.data?.detail || "Re-draft failed");
                                        } finally { setBusyId(null); }
                                      }}
                                      disabled={busyId === p.id}
                                      className="byrd-btn byrd-btn-outline text-xs disabled:opacity-60"
                                      data-testid={`redraft-${p.id}`}
                                    >Ask Ada to Rewrite</button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="text-sm font-semibold mb-1">Subject: {p.draft_subject}</div>
                                  <pre className="text-sm text-[#2A2A2A] whitespace-pre-wrap font-sans border-l-2 border-[#C89434] pl-3">{p.draft_body}</pre>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {addOpen && <AddDialog audience={audience} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
      {settingsOpen && (
        <InstantlySettingsDialog
          audience={audience}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => { loadInstantly(); }}
        />
      )}
    </div>
  );
}

function AddDialog({ audience = "lender", onClose, onSaved }) {
  const [f, setF] = useState({
    institution: "", state: "TX", hq_city: "", website: "",
    contact_name: "", contact_title: "", contact_email: "", contact_phone: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.institution.trim()) return toast.error("Institution required");
    setBusy(true);
    try {
      await api.post("/admin/marketplace/prospects", { ...f, audience });
      toast.success("Prospect added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1A1A1A]/50 grid place-items-center p-4" onClick={onClose}>
      <div className="byrd-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}
           data-testid="add-prospect-dialog">
        <h2 className="font-serif text-2xl font-bold">Add prospect</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <Fld label="Institution *"><Inp value={f.institution} onChange={set("institution")} data-testid="ap-institution" /></Fld>
          <Fld label="State *">
            <select value={f.state} onChange={set("state")} data-testid="ap-state"
                    className="w-full h-10 px-3 border border-[#E4DFD1] bg-white rounded-md text-sm">
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Fld>
          <Fld label="HQ City"><Inp value={f.hq_city} onChange={set("hq_city")} /></Fld>
          <Fld label="Website"><Inp value={f.website} onChange={set("website")} placeholder="https://…" /></Fld>
          <Fld label="Contact Name"><Inp value={f.contact_name} onChange={set("contact_name")} /></Fld>
          <Fld label="Title"><Inp value={f.contact_title} onChange={set("contact_title")} /></Fld>
          <Fld label="Email"><Inp value={f.contact_email} onChange={set("contact_email")} data-testid="ap-email" /></Fld>
          <Fld label="Phone"><Inp value={f.contact_phone} onChange={set("contact_phone")} /></Fld>
        </div>
        <Fld label="Notes" className="mt-3">
          <textarea value={f.notes} onChange={set("notes")} rows={2}
                    className="w-full px-3 py-2 border border-[#E4DFD1] bg-white rounded-md text-sm" />
        </Fld>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="byrd-btn byrd-btn-outline">Cancel</button>
          <button onClick={save} disabled={busy} className="byrd-btn byrd-btn-dark disabled:opacity-60" data-testid="ap-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Fld = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1">{label}</div>
    {children}
  </label>
);
const Inp = (p) => (
  <input {...p} className="w-full h-10 px-3 border border-[#E4DFD1] bg-white rounded-md text-sm focus:outline-none focus:border-[#C89434]" />
);

// ─── Instantly.ai: connection badge ────────────────────────────────────────
function InstantlyBadge({ data, onClick }) {
  const status = !data
    ? { label: "Checking…", cls: "bg-[#F3EEE0] text-[#6B6558]", Icon: Wifi }
    : !data.configured
      ? { label: "Instantly: Not Configured", cls: "bg-[#FADCDA] text-[#8A1F1A]", Icon: WifiOff }
      : !data.ok
        ? { label: "Instantly: Error", cls: "bg-[#FADCDA] text-[#8A1F1A]", Icon: WifiOff }
        : !data.default_campaign_id
          ? { label: "Instantly: No Campaign", cls: "bg-[#FBEFD3] text-[#7A5410]", Icon: Wifi }
          : { label: "Instantly: Connected", cls: "bg-[#E5F1E5] text-[#245C25]", Icon: Wifi };
  const { Icon } = status;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="instantly-status-badge"
      title={data?.error || "Click to configure"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${status.cls}`}
    >
      <Icon size={12} /> {status.label}
    </button>
  );
}

// ─── Instantly.ai: settings dialog ─────────────────────────────────────────
const AUDIENCE_LIST = ["lender", "investor", "builder", "developer", "listing_agent"];

function InstantlySettingsDialog({ audience, onClose, onSaved }) {
  const [status, setStatus] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [defaultId, setDefaultId] = useState("");
  const [perAudience, setPerAudience] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c, a] = await Promise.all([
        api.get("/admin/instantly/status"),
        api.get("/admin/instantly/campaigns"),
        api.get("/admin/instantly/accounts"),
      ]);
      setStatus(s.data);
      setDefaultId(s.data?.default_campaign_id || "");
      setPerAudience(s.data?.campaigns_by_audience || {});
      setCampaigns(c.data?.items || []);
      setAccounts(a.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Instantly settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/instantly/settings", {
        default_campaign_id: defaultId || null,
        campaigns_by_audience: perAudience,
      });
      toast.success("Instantly settings saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const dot = (ok) => (
    <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-[#245C25]" : "bg-[#8A1F1A]"}`} />
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A1A1A]/50 grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="byrd-card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="instantly-settings-dialog"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">
              // Outreach · Instantly.ai
            </div>
            <h2 className="font-serif text-2xl font-bold mt-1">Outreach Settings.</h2>
            <p className="text-xs text-[#6B6558] mt-1 max-w-md">
              Approved prospects are pushed as leads into the Instantly campaign you
              pick below. Delivery is handled by Instantly per the campaign schedule.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6B6558] hover:text-[#1A1A1A]" data-testid="instantly-close">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-[#6B6558]">Checking Instantly…</div>
        ) : (
          <div className="mt-5 space-y-5">
            {/* Connection state */}
            <div className="byrd-card p-4 border-l-4 border-[#C89434]">
              <div className="flex items-center gap-2">
                {dot(!!status?.ok)}
                <span className="text-sm font-semibold">
                  {status?.ok ? "Connected to Instantly" : "Not Connected"}
                </span>
                <button onClick={refresh} className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#C89434] hover:underline">
                  <RefreshCw size={10} className="inline" /> Refresh
                </button>
              </div>
              {!status?.ok && status?.error && (
                <div className="text-[11px] text-[#8A1F1A] mt-1">{status.error}</div>
              )}
            </div>

            {/* Sending accounts */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mb-1.5">
                // Sending Accounts ({accounts.length})
              </div>
              {accounts.length === 0 ? (
                <div className="text-xs text-[#6B6558] italic">
                  No sending accounts connected in Instantly yet. Connect one in
                  Instantly → Email Accounts before sending.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {accounts.map((a) => (
                    <div key={a.email} className="border border-[#E4DFD1] bg-white rounded-md p-2.5 text-xs">
                      <div className="font-semibold">{a.email}</div>
                      <div className="text-[#6B6558] text-[10px] mt-0.5">
                        Daily cap: {a.daily_limit}
                        {a.warmup_status === 1 && <> · <span className="text-[#245C25]">Warming</span></>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Default campaign picker */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mb-1.5">
                // Default Campaign (used when no per-audience override is set)
              </div>
              {campaigns.length === 0 ? (
                <div className="rounded-md border border-dashed border-[#C89434] bg-[#FBF8F1] p-4 text-sm">
                  <div className="font-semibold text-[#1A1A1A]">No campaigns found in your Instantly account.</div>
                  <ol className="list-decimal pl-5 mt-2 space-y-1 text-xs text-[#6B6558]">
                    <li>Go to Instantly → <b>Campaigns</b> → <b>New Campaign</b>.</li>
                    <li>Subject line: <code className="bg-white px-1 rounded">{"{{approved_subject}}"}</code></li>
                    <li>Body step 1: <code className="bg-white px-1 rounded">{"{{personalization}}"}</code></li>
                    <li>Attach your sending account, pick a schedule, and <b>Activate</b>.</li>
                    <li>Come back and tap <b>Refresh</b> — the campaign will appear here.</li>
                  </ol>
                  <a
                    href="https://app.instantly.ai/app/campaigns"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-[11px] text-[#C89434] hover:underline"
                  >
                    Open Instantly Campaigns <ExternalLink size={11} />
                  </a>
                </div>
              ) : (
                <select
                  value={defaultId}
                  onChange={(e) => setDefaultId(e.target.value)}
                  data-testid="instantly-default-campaign"
                  className="w-full h-10 px-3 border border-[#E4DFD1] bg-white rounded-md text-sm"
                >
                  <option value="">— Select a campaign —</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.status_label})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Per-audience overrides */}
            {campaigns.length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558] mb-1.5">
                  // Per-Audience Overrides (optional)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {AUDIENCE_LIST.map((aud) => (
                    <label key={aud} className="block">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1">
                        {aud.replace("_", " ")}
                      </div>
                      <select
                        value={perAudience[aud] || ""}
                        onChange={(e) =>
                          setPerAudience((s) => ({ ...s, [aud]: e.target.value }))
                        }
                        data-testid={`instantly-audience-${aud}`}
                        className="w-full h-9 px-2 border border-[#E4DFD1] bg-white rounded-md text-sm"
                      >
                        <option value="">Use default</option>
                        {campaigns.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="byrd-btn byrd-btn-outline">Cancel</button>
          <button
            onClick={save}
            disabled={busy || loading}
            className="byrd-btn byrd-btn-dark disabled:opacity-60"
            data-testid="instantly-save"
          >
            {busy ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
